import { MagicVariables } from '@/types';
import { HTMXEvent } from '@/events/types';
import { IJumpToBottom } from './jump_to_bottom';
import { IContentWrapper } from './content_wrapper';
import { IRecentSearches } from './recent_searches';
import { ILoadingHandler } from './loading_handler';
import { ISelectionHandler } from './selection_handler';
import { ITabs } from './tabs';
import { redirect } from '@/utils/general';

export const MIN_SEARCH_LENGTH = 2;
export const FETCH_SEARCH_EVENT_NAME = 'triggerHTMXSearchResults';

export class SearchBox implements MagicVariables {
    $dispatch: MagicVariables['$dispatch'];
    $refs?: MagicVariables['$refs'];
    $nextTick?: MagicVariables['$nextTick'];

    jumpToBottom: IJumpToBottom;
    contentWrapper: IContentWrapper;
    recentSearches: IRecentSearches;
    loadingHandler: ILoadingHandler;
    selectionHandler: ISelectionHandler;
    tabs: ITabs;

    hideExplicitContent = true;
    isBannedKWEntered = false;
    isLoading = false;
    isClearVisible = false;
    previousSearch = '';

    constructor(
        hideExplicitContent: boolean,
        jumpToBottom: IJumpToBottom,
        contentWrapper: IContentWrapper,
        recentSearches: IRecentSearches,
        loadingHandler: ILoadingHandler,
        selectionHandler: ISelectionHandler,
        tabs: ITabs
    ) {
        this.hideExplicitContent = hideExplicitContent;
        this.jumpToBottom = jumpToBottom;
        this.contentWrapper = contentWrapper;
        this.recentSearches = recentSearches;
        this.loadingHandler = loadingHandler;
        this.selectionHandler = selectionHandler;
        this.tabs = tabs;
    }

    init() {
        this.jumpToBottom.setContainer(this.$refs.searchResults);
        this.contentWrapper.setJumpToBottom(this.jumpToBottom);

        this.fetchSearchResults();
        this.setClearButtonVisibility();
    }

    dispatchSearch() {
        this.$dispatch(FETCH_SEARCH_EVENT_NAME);
    }

    fetchSearchResults(keyword?: string) {
        // If no keyword is provided, API returns Trending categories
        // If keyword is provided, it should be minimum MIN_SEARCH_LENGTH characters long
        if (!keyword?.length || keyword.length >= MIN_SEARCH_LENGTH) {
            this.dispatchSearch();
        }

        this.previousSearch = keyword || '';
    }

    onInput() {
        const searchInputValue = this.getSearchInputValue();

        if (this.previousSearch === searchInputValue) {
            return;
        }

        this.fetchSearchResults(searchInputValue);
        this.setClearButtonVisibility();
    }

    onClick() {
        this.contentWrapper.open();
    }

    onSearchClick() {
        this.redirectToSearchPage(this.getSearchInputValue());
    }

    onSearchResultClick(keyword: string) {
        this.recentSearches.saveSearch(keyword);
    }

    onClearClick() {
        this.setSearchInputValue('');
        this.setClearButtonVisibility();
        this.fetchSearchResults();
        this.selectionHandler.resetSelection();
    }

    onCancelClick() {
        this.contentWrapper.close();
    }

    handleSearchRedirection(keyword: string) {
        this.recentSearches.saveSearch(keyword);
        this.redirectToSearchPage(keyword);
    }

    handleBeforeRequest() {
        this.loadingHandler.startTimeout();
    }

    handleSuccessfulRequest(event: HTMXEvent) {
        this.isBannedKWEntered = !event.detail.xhr.response;

        this.jumpToBottom.setVisibility();
        this.recentSearches.refresh();
        this.loadingHandler.stopTimeout();

        if (this.isBannedKWEntered) {
            this.selectionHandler.resetSelection();
        }

        // Only refresh the keyboard handler on the next Alpine tick
        this.$nextTick(() => {
            this.selectionHandler.refresh();
        });
    }

    handleAfterRequest(event: HTMXEvent) {
        // If a new request is started before the previous has finished
        // HTMX will cancel the previous request. In this case xhr.status is 0.
        const isRequestAborted = event.detail.xhr.status === 0;

        if (isRequestAborted) {
            return;
        }

        this.handleSuccessfulRequest(event);
    }

    redirectToSearchPage(keyword: string) {
        redirect('/s/' + encodeURIComponent(keyword) + '/');
    }

    redirectByKeyboardSelectedItem(element: HTMLAnchorElement) {
        if (element.href) {
            redirect(element.href);
        } else {
            this.redirectToSearchPage(this.selectionHandler.getSelectedItemText());
        }
    }

    setClearButtonVisibility(): void {
        this.isClearVisible = this.getSearchInputValue().length >= MIN_SEARCH_LENGTH;
    }

    getSearchInputValue(): string {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        return searchInput ? searchInput.value.trim() : '';
    }

    setSearchInputValue(value: string): void {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.value = value;
    }

    focusSearch() {
        // Focus search and jump cursor to the end of the input
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.focus();
        searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
    }

    clearSearchFocus() {
        const searchInput = this.$refs.searchInput as HTMLInputElement;
        searchInput.blur();
    }

    isInputSameAsSelectedText(): boolean {
        const searchInputValue = this.getSearchInputValue();
        const activeElement = this.selectionHandler.getActiveElement();

        return activeElement.innerText.trim().toLowerCase() === searchInputValue.toLowerCase();
    }

    onTabClick(tab: string) {
        this.tabs.setActive(tab);
        this.jumpToBottom.setVisibility();
        this.$nextTick(() => this.selectionHandler.onTabChange());
        this.focusSearch();
    }

    onEnterPress() {
        const activeElement = this.selectionHandler.getActiveElement();
        const searchInputValue = this.getSearchInputValue();

        if (this.isInputSameAsSelectedText()) {
            this.recentSearches.saveSearch(this.selectionHandler.getSelectedItemText());
            this.redirectByKeyboardSelectedItem(activeElement);
        } else {
            this.recentSearches.saveSearch(searchInputValue);
            this.redirectToSearchPage(searchInputValue);
        }
    }

    // TODO: Make horizontalPress fn or something like that, because the implementation is the same for both buttons
    onLeftPress() {
        const searchInputValue = this.getSearchInputValue();

        if (this.isInputSameAsSelectedText() && this.previousSearch !== searchInputValue) {
            this.fetchSearchResults(searchInputValue);
        }

        this.setClearButtonVisibility();
    }

    // TODO: Make horizontalPress fn or something like that, because the implementation is the same for both buttons
    onRightPress() {
        const searchInputValue = this.getSearchInputValue();

        if (this.isInputSameAsSelectedText() && this.previousSearch !== searchInputValue) {
            this.fetchSearchResults(searchInputValue);
        }

        this.setClearButtonVisibility();
    }

    // TODO: Make verticalPress fn or something like that, because the implementation is the same for both buttons,
    // except the focus call on the input
    onUpPress() {
        this.selectionHandler.onUpPress();
        this.setSearchInputValue(this.selectionHandler.getSelectedItemText());
        this.setClearButtonVisibility();

        // Naturally when pressing up arrow the caret jumps to the beginning of the input.
        // We want to keep the caret at the end of the input.
        this.$nextTick(() => this.focusSearch());
    }

    // TODO: Make verticalPress fn or something like that, because the implementation is the same for both buttons,
    // except the focus call on the input
    onDownPress() {
        this.selectionHandler.onDownPress();
        this.setSearchInputValue(this.selectionHandler.getSelectedItemText());
        this.setClearButtonVisibility();
    }

    onEscPress() {
        this.contentWrapper.close();
        this.clearSearchFocus();
    }
}
